import isUndefined from 'lodash-es/isUndefined'
import { reverse } from 'named-urls'
import React from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { ProductItemGroupedDetails } from '@components/common/ProductItemGroupedDetails/ProductItemGroupedDetails'
import { SideSummary } from '@components/common/SideSummary'
import {
  BackButton,
  Button,
  ButtonClean,
  Checkbox,
  Division,
  HeadContent,
  Heading,
  Loading,
  PriceString,
  ProductsList,
  Summary,
  useModalContext,
} from '@fourthwall/components'
import type { OrderItemsReplacements } from '@fourthwall/services/dist/OrderApiClient'
import type { Price } from '@fourthwall/services/dist/models/common'
import type { NonCancellableOrderOffer, OrderOffer } from '@fourthwall/services/dist/models/order'
import { routing } from '@utils/routing'

import type { Order } from '../../Order.types'
import { getNonCancellableReason } from '../../utils/getNonCancellableReason'
import type { EditableOffer } from './OrderEdit.types'
import * as S from './OrderEditView.styled'
import { OrderEditConfirmModal } from './components/OrderEditConfirmModal/OrderEditConfimModal'
import { ProductItemSummary } from './components/ProductItemSummary/ProductItemSummary'
import { OrderVariantsModal } from './modules/OrderVariantsModal'
import { aggregateOffers } from './utils/aggregateOffers'

type OrderEditViewProps = {
  orderId: string
  friendlyId?: string
  editableOffers?: EditableOffer[]
  nonEditableOffers?: NonCancellableOrderOffer[]
  replacements: OrderItemsReplacements[]
  promotion?: Order['promotion']
  donation: Price | undefined
  subtotal?: Price
  shipping?: Price
  taxes?: Price
  discount?: Price
  total?: Price
  paidTotal?: Price
  isEditButtonDisabled: boolean
  isLoading: boolean
  shouldRestock: boolean
  onVariantEdit: (editableOffer: EditableOffer) => void
  onOrderEdit: (reason?: string) => void
  onRestockChange: (value: boolean) => void
}

export const OrderEditView = ({
  orderId,
  friendlyId = '',
  editableOffers,
  nonEditableOffers,
  replacements,
  donation,
  subtotal,
  shipping,
  taxes,
  discount,
  total,
  paidTotal,
  shouldRestock,
  isEditButtonDisabled,
  isLoading,
  onVariantEdit,
  onOrderEdit,
  onRestockChange,
}: OrderEditViewProps) => {
  const { open } = useModalContext()
  const currentOffersLength = editableOffers?.reduce(
    (sum, offer) => sum + offer.current.quantity,
    0,
  )
  const aggregatedOffers = aggregateOffers(
    editableOffers?.map((editableOffer) => editableOffer.current),
  )

  const summaryContent = (
    <>
      {!isUndefined(subtotal) && editableOffers && (
        <Summary.Row label={`Subtotal (${currentOffersLength} items)`} value={subtotal} />
      )}
      {!isUndefined(shipping) && <Summary.Row label="Shipping" value={shipping} />}
      {!isUndefined(taxes) && taxes?.value > 0 && <Summary.Row label="Sales tax" value={taxes} />}
      {!isUndefined(discount) && discount?.value > 0 && (
        <Summary.Row
          label="Discount"
          value={{
            // eslint-disable-next-line no-unsafe-optional-chaining
            value: discount?.value * -1,
            currency: discount.currency,
          }}
        />
      )}
      {!isUndefined(total) && (
        <>
          <S.SummaryDivider />
          <Summary.Row label="Total" value={total} />
        </>
      )}
      {!isUndefined(paidTotal) && <Summary.Row label="Paid by customer" value={paidTotal} />}
      {!isUndefined(total) && !isUndefined(paidTotal) && paidTotal > total && (
        <Summary.Row
          label="Remaining amount"
          value={{
            value: paidTotal.value - total.value,
            currency: paidTotal.currency,
          }}
          bold
        />
      )}
      {!isUndefined(total) && !isUndefined(paidTotal) && total.value > paidTotal.value && (
        <Summary.Row
          label="Additional charge"
          value={{
            value: total.value - paidTotal.value,
            currency: paidTotal.currency,
          }}
          bold
        />
      )}
    </>
  )

  const openOrderVariantsModal = (editableOffer: EditableOffer) => {
    open(OrderVariantsModal, {
      offer: editableOffer.current,
      replacements,
      onConfirm: (offer) => {
        onVariantEdit({
          ...editableOffer,
          current: offer,
        })
      },
    })
  }

  const handleRevertVariantChange = (editableOffer: EditableOffer) => {
    onVariantEdit({
      ...editableOffer,
      current: editableOffer.original,
    })
  }

  const isEditableOffer = (
    offer: OrderOffer | NonCancellableOrderOffer | EditableOffer,
  ): offer is EditableOffer => {
    return 'current' in offer
  }

  const renderOffer = (
    orderOffer: OrderOffer | NonCancellableOrderOffer | EditableOffer,
    withFooter: boolean,
  ) => {
    const currentOffer = isEditableOffer(orderOffer) ? orderOffer.current : orderOffer

    const { variant, offer, quantity, price, grouped } = currentOffer
    const shouldRenderSummary =
      isEditableOffer(orderOffer) && orderOffer.original.variantId !== orderOffer.current.variantId

    return (
      <Division size="small" key={isEditableOffer(orderOffer) ? orderOffer.id : offer.id}>
        <ProductsList.Item
          name={offer.name}
          description={variant.attributesDescription}
          type={offer.offerType === 'GIFT_CARD' ? 'giftCard' : undefined}
          tag={
            !isEditableOffer(orderOffer) && 'nonCancellableReason' in orderOffer
              ? getNonCancellableReason(orderOffer.nonCancellableReason)
              : undefined
          }
          image={offer.thumbnailImage}
          quantity={quantity}
          price={price}
          details={grouped ? <ProductItemGroupedDetails groupedDetails={grouped} /> : undefined}
          footer={
            withFooter && isEditableOffer(orderOffer) ? (
              <S.ProductItemFooter>
                {shouldRenderSummary && (
                  <S.ProductItemFooterSummary>
                    <ProductItemSummary
                      label={`Changed variant from ${orderOffer.original.variant.attributesDescription} to
                      ${orderOffer.current.variant.attributesDescription}`}
                      onRemoveModificationClick={() => handleRevertVariantChange(orderOffer)}
                    />
                  </S.ProductItemFooterSummary>
                )}
                <ButtonClean
                  label="Edit variant"
                  appearance="secondary"
                  onClick={() => openOrderVariantsModal(orderOffer)}
                />
              </S.ProductItemFooter>
            ) : undefined
          }
        />
      </Division>
    )
  }

  const renderDonation = () => {
    if (!donation) return null

    return (
      <Division size="small">
        <ProductsList.Item name="Donation" type="donation" quantity={1} price={donation} />
      </Division>
    )
  }

  const renderOffers = (
    offers: OrderOffer[] | NonCancellableOrderOffer[] | EditableOffer[],
    withFooter: boolean,
  ) => {
    return offers?.map((offer) => renderOffer(offer, withFooter))
  }

  const renderTotalDifferenceNotice = () => {
    if (!isUndefined(total) && !isUndefined(paidTotal) && paidTotal.value > total.value) {
      return (
        <>
          Remaining amount (
          <PriceString value={paidTotal.value - total.value} currency={paidTotal.currency} />) will
          be added to your profits
        </>
      )
    }

    if (!isUndefined(total) && !isUndefined(paidTotal) && total.value > paidTotal.value) {
      return (
        <>
          Charge amount (<PriceString value={total.value - paidTotal.value} />) will be deducted
          from your profits
        </>
      )
    }

    return null
  }

  const handleEditConfirm = (reason?: string) => {
    onOrderEdit(reason)
  }

  const handleEditOrderClick = () => {
    if (!aggregatedOffers?.length) {
      return
    }

    open(OrderEditConfirmModal, {
      content: (
        <>
          <Division size="small" />
          <Division size="small">
            <ProductsList>{renderOffers(aggregatedOffers, false)}</ProductsList>
          </Division>
          <Division size="small">
            <Summary>{summaryContent}</Summary>
          </Division>
        </>
      ),
      footer:
        total !== paidTotal ? (
          <>
            {renderTotalDifferenceNotice()}. Supporter will be notified about the changes via email.
          </>
        ) : null,
      onConfirm: handleEditConfirm,
    })
  }

  const leftContent = (
    <>
      {editableOffers && editableOffers.length > 0 && (
        <S.Section>
          <Division size="small">
            <ProductsList>{renderOffers(editableOffers, true)}</ProductsList>
          </Division>
          <Division size="small" />
        </S.Section>
      )}
      {(donation || (nonEditableOffers && nonEditableOffers.length > 0)) && (
        <S.Section>
          <S.SectionHeader>
            <Heading type="h5" weight="semibold">
              Items that cannot be edited
            </Heading>
          </S.SectionHeader>
          <Division size="small">
            <ProductsList>
              {nonEditableOffers && nonEditableOffers.length > 0 && (
                <>{renderOffers(nonEditableOffers, false)}</>
              )}
              {renderDonation()}
            </ProductsList>
          </Division>
          <Division size="small" />
        </S.Section>
      )}
    </>
  )

  const summary = (
    <SideSummary>
      <S.SummaryHeader>Summary</S.SummaryHeader>
      <Summary small>{summaryContent}</Summary>
      {!isUndefined(total) && !isUndefined(paidTotal) && total !== paidTotal && (
        <S.SummarySubsection>{renderTotalDifferenceNotice()}</S.SummarySubsection>
      )}
      <S.SummaryButton>
        <Button
          label="Update order"
          appearance="primary"
          fullWidth
          disabled={isEditButtonDisabled}
          onClick={handleEditOrderClick}
        />
      </S.SummaryButton>
      <S.SummaryCheckbox>
        <Checkbox value={shouldRestock} label="Restock items" onValueChange={onRestockChange} />
      </S.SummaryCheckbox>
    </SideSummary>
  )

  return (
    <>
      <HeadContent title={`Edit order (#${friendlyId})`} />
      <Loading isLoading={isLoading}>
        <PageLayout leftContent={leftContent} rightContent={summary}>
          <Dashboard.ItemViewHeader
            name="Edit order"
            backButton={
              <BackButton
                label={`Order #${friendlyId}`}
                to={{ pathname: reverse(routing.contributions.orders.order.self, { orderId }) }}
              />
            }
          />
        </PageLayout>
      </Loading>
    </>
  )
}
